import styled from '@emotion/styled';
import { ResizeObserver } from '@juggle/resize-observer';
import get from 'lodash.get';
import React, { useState } from 'react';
import { animated, useSpring } from 'react-spring';
import useMeasure from 'react-use-measure';

import { RevealContext } from '../../hooks/useReveal';
import { mobileGutterPadding } from '../../styles';
import { device } from '../../styles/devices';
import { typography } from '../../styles/typography';
import { shouldForwardProp } from '../../utils';
import { ButtonPlus } from '../buttons/ButtonPlus';

interface IProps {
  title?: string;
  content?: React.ReactNode;
  category?: React.ReactNode;
  isFullWidth?: boolean;
  lineSeperator?: boolean;
  contentSpacing?: boolean;
  onMouseEnter?: any;
  onMouseLeave?: any;
  isOpen?: boolean;
  onOpen?(index: number): void;
  index?: number;
  onClose?(index: number): void;
}

interface IAccordionProp {
  isFullWidth?: boolean;
  lineSeperator?: boolean;
  contentSpacing?: boolean;
  isOpen?: boolean;
}

const Item = styled.div<IAccordionProp>`
  ${(props) =>
    !props.lineSeperator &&
    `border-bottom: 1px solid ${props.theme.colors.primary};`};

  ${(props) =>
    props.isOpen && !props.lineSeperator && 'border-color: transparent'};

  transition: border-color 200ms ease-in-out;
`;

const Header = styled.button<IAccordionProp>`
  display: flex;
  align-items: center;
  justify-content: space-between;

  position: relative;
  padding: ${(props) => `25px ${props.theme.breakpoints.sm.gutter}px`};
  width: 100%;

  /* border: none; */

  text-align: left;

  background-color: transparent;
  appearance: none;
  cursor: pointer;

  ${(props) =>
    props.lineSeperator &&
    `border-bottom: 1px solid ${props.theme.colors.primary};`}

  @media ${device.tablet} {
    padding: 27px 45px 27px 0;
  }
`;

const Content = styled(animated.div, {
  shouldForwardProp: shouldForwardProp('div'),
})<{ isFullWidth?: boolean }>`
  padding: ${(props) =>
    props.isFullWidth ? '0' : `0 ${props.theme.breakpoints.sm.gutter}px`};
  height: 0px;
  overflow: hidden;
  ${mobileGutterPadding}

  @media ${device.tablet} {
    padding: ${(props) => (props.isFullWidth ? '0' : '0 45px 0 0')};
  }
`;

const Heading = styled.h3`
  ${typography.h4};
  margin-right: 40px;
`;

const AccordionMarkdown = styled(animated.div, {
  shouldForwardProp: shouldForwardProp('div'),
})<IAccordionProp>`
  position: relative;
  padding: ${(props) =>
    props.lineSeperator ? `0 ${props.theme.containerGutter}px` : '40px 0'};
  ${(props) => props.contentSpacing && 'padding: 40px 20px 60px;'};
  width: 100%;
  opacity: 0;

  @media ${device.mobile} {
    padding: ${(props) =>
      props.lineSeperator
        ? `0 ${props.theme.breakpoints.sm.gutter}px`
        : '40px 0'};
  }

  @media ${device.tablet} {
    padding: ${(props) => (props.lineSeperator ? '0' : '40px 0')};
  }

  @media (min-width: 600px) {
    ${(props) => !props.isFullWidth && `width: 80%;`}
  }
  @media ${device.desktop} {
    ${(props) => props.contentSpacing && 'padding: 43px 43px 60px;'};
  }

  &:before {
    content: '';
    display: ${(props) => (props.lineSeperator ? 'none' : 'block')};
    position: absolute;
    top: 0;
    left: ${(props) => (props.isFullWidth ? '20px' : '0')};

    height: 1px;
    width: 27px;

    background-color: ${(props) => props.theme.colors.foreground};

    @media ${device.tablet} {
      left: 0;
    }
  }
`;

export const AccordionItem = ({
  index,
  title,
  content,
  isFullWidth,
  lineSeperator,
  contentSpacing,
  onMouseEnter,
  onMouseLeave,
  onClose,
  onOpen,
  isOpen,
}: // isDeafultOpen = false,
IProps) => {
  // const [isOpen, setIsOpen] = useState(isDeafultOpen);
  const [hover, setHover] = useState(false);
  const [copyRef, copySize] = useMeasure({ polyfill: ResizeObserver });

  // const toggle = () => {
  //   setIsOpen((value) => {
  //     props.
  //     return !value;
  //   });
  // };

  const setHeight = useSpring({
    height: isOpen ? `${get(copySize, 'height', 0) as number}px` : '0px',
    visability: isOpen ? 'visible' : 'hidden',
  });

  const fadeIn = useSpring({
    opacity: isOpen ? 1 : 0,
    transform: isOpen ? 'translateY(0px)' : 'translateY(10px)',
  });

  const onEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    setHover(true);
    if (onMouseEnter) {
      onMouseEnter(e);
    }
  };

  const onLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    setHover(false);
    if (onMouseLeave) {
      onMouseLeave(e);
    }
  };

  return (
    <Item lineSeperator={lineSeperator} isOpen={isOpen}>
      <Header
        onClick={() => {
          if (onClose && onOpen && index !== undefined) {
            return isOpen ? onClose(index) : onOpen(index);
          }
        }}
        role="button"
        aria-expanded={isOpen ? true : false}
        aria-controls="content"
        isFullWidth={isFullWidth}
        lineSeperator={lineSeperator}
        onMouseLeave={onLeave}
        onMouseEnter={onEnter}
      >
        <Heading>{title}</Heading>
        <ButtonPlus isOpen={!!isOpen} isHover={hover} />
      </Header>

      <Content
        className={isOpen ? 'open' : ''}
        style={
          { ...setHeight, visibility: isOpen ? 'visible' : 'hidden' } as any
        }
        id="content"
        isFullWidth={isFullWidth}
      >
        <AccordionMarkdown
          ref={copyRef}
          isFullWidth={isFullWidth}
          lineSeperator={lineSeperator}
          contentSpacing={contentSpacing}
          style={{ ...fadeIn } as any}
        >
          <RevealContext.Provider value={{ enabled: !!isOpen }}>
            {content}
          </RevealContext.Provider>
        </AccordionMarkdown>
      </Content>
    </Item>
  );
};
